// The Tester switch of the player page (spec/dashboard.md section 11.2; spec/api-conventions.md section 21.7): PATCH
// …/players/{player} { tester } with If-Match on the player's version; a tester's events stay in the feed but every counter
// and export leaves them out unless the read includes testers (Q-J-11). Audited as player.update.
import { useState } from 'react';
import { etagOfVersion } from '../../api/client';
import { ops } from '../../api/operations';
import type { Player } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import { playerPrefix } from '../../players/usePlayer';
import { useGame } from '../../shell/GameContext';
import { Switch } from '../../ui/inputs';
import { useToasts } from '../../ui/Toast';
import { queryCache } from '../../workspace/cache';
import { toastCommandError } from './commands';

export function TesterSwitch({ env, player, writeMissing }: { env: string; player: Player; writeMissing: string | null }) {
  const { client } = useAuth();
  const { org, game } = useGame();
  const toasts = useToasts();
  const [busy, setBusy] = useState(false);
  const change = async (tester: boolean) => {
    setBusy(true);
    try {
      const updated = (await client.data(ops.updatePlayer, { path: { org, game: game.slug, env, player: player.id }, body: { tester }, ifMatch: etagOfVersion(player.version) ?? null })) as Player;
      queryCache.set(`${playerPrefix(game.id, env, player.id)}:player`, updated);
      toasts.success(tester ? 'Marked as a tester: excluded from the analytics counters and exports' : 'No longer a tester: counted again from now on');
    } catch (e) {
      toastCommandError(toasts, e);
    } finally {
      setBusy(false);
    }
  };
  const title = writeMissing ? `You lack the '${writeMissing}' permission.` : 'A tester is excluded from every analytics counter and export unless a read includes testers';
  return (
    <span className="tester-switch" title={title} data-testid="tester-switch">
      <Switch label="Tester" checked={!!player.tester} disabled={!!writeMissing || busy} onChange={(v) => void change(v)} />
    </span>
  );
}
